import './styles/main.css'
import { loadComponents } from './scripts/load-components'
import { initReelAnimation } from './scripts/reel-animation'
import { initVerticalTabs } from './scripts/vertical-tabs'
import { initScrollAnimations } from './scripts/scroll-animations'
import { initTestimonialAnimation } from './scripts/testimonial-animation'
import { initDemoForm } from './scripts/demo-form'
import { initParticleField } from './scripts/particle-field'
import { NAV_HEIGHT } from './config/constants'

// ============================================
// HERO BACKGROUND
// ============================================
// Use particle field animation for hero background
// ============================================

/**
 * Initialize all interactive components on page load
 */
async function init() {
  // Initialize application

  // Enable automatic scroll restoration for browser back/forward
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'auto'
  }

  // Load shared components (header, footer) first
  await loadComponents()

  // Initialize reel animation
  initReelAnimation()

  // Initialize hero background with particle field
  initParticleField()

  // Initialize vertical tabs for use cases
  initVerticalTabs()

  // Initialize GSAP scroll animations
  initScrollAnimations()

  // Initialize testimonial color wave animation
  initTestimonialAnimation()

  // Initialize demo form modal
  initDemoForm()

  // Initialize hero video with delayed autoplay
  initHeroVideo()

  // Smooth scroll for anchor links
  initSmoothScroll()

  // Handle "Coming Soon" for API/Playground links
  initComingSoonLinks()

  // Initialize mobile menu
  initMobileMenu()
}

/**
 * Initialize hero video with delayed autoplay
 * Starts playing the video after 1 second for better UX
 */
function initHeroVideo() {
  const video = document.getElementById('heroVideo') as HTMLVideoElement
  if (!video) return

  // Delay autoplay by 1 second
  setTimeout(() => {
    video.play().catch((error) => {
      console.error('Video autoplay failed:', error)
    })
  }, 1000)
}

/**
 * Enable smooth scrolling for navigation anchor links
 * Accounts for fixed navigation height when scrolling to sections
 */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
      e.preventDefault()
      const targetId = this.getAttribute('href')
      if (!targetId || targetId === '#') return

      const targetElement = document.querySelector(targetId)
      if (targetElement) {
        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        })
      }
    })
  })
}

/**
 * Initialize "Coming Soon" modal for Playground links
 * Shows a modal when users click on Playground links, with option to schedule demo
 * Supports multiple close methods: X button, dismiss button, click outside, Escape key
 */
function initComingSoonLinks() {
  const modalElement = document.getElementById('comingSoonModal')
  if (!modalElement) return

  const closeButton = modalElement.querySelector('.modal-close')
  const dismissButton = modalElement.querySelector('.modal-dismiss')

  // Show modal function
  function showModal() {
    modalElement!.style.display = 'flex'
    // Trigger reflow to enable transition
    modalElement!.offsetHeight
    modalElement!.classList.add('active')
    document.body.style.overflow = 'hidden'
  }

  // Hide modal function
  function hideModal() {
    modalElement!.classList.remove('active')
    document.body.style.overflow = ''
    setTimeout(() => {
      modalElement!.style.display = 'none'
    }, 300) // Match transition duration
  }

  // Open modal when clicking Playground links (both #playground and /#playground)
  document.querySelectorAll('a[href="#playground"], a[href="/#playground"]').forEach(link => {
    link.addEventListener('click', function(e) {
      e.preventDefault()
      showModal()
    })
  })

  // Close modal on close button click
  closeButton?.addEventListener('click', hideModal)

  // Close modal on dismiss button click
  dismissButton?.addEventListener('click', hideModal)

  // Close modal when clicking overlay background
  modalElement.addEventListener('click', function(e) {
    if (e.target === modalElement) {
      hideModal()
    }
  })

  // Close modal on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modalElement.classList.contains('active')) {
      hideModal()
    }
  })
}

/**
 * Initialize mobile hamburger menu
 * Toggles mobile menu visibility when hamburger button is clicked
 */
function initMobileMenu() {
  const hamburger = document.querySelector('.nav__hamburger')
  const navLinks = document.querySelector('.nav__links')

  if (!hamburger || !navLinks) return

  hamburger.addEventListener('click', () => {
    hamburger.classList.toggle('active')
    navLinks.classList.toggle('active')
  })

  // Close menu when clicking a link
  navLinks.querySelectorAll('.nav__link, .nav__cta').forEach(link => {
    link.addEventListener('click', () => {
      hamburger.classList.remove('active')
      navLinks.classList.remove('active')
    })
  })

  // Close menu when clicking outside
  document.addEventListener('click', (e) => {
    if (!hamburger.contains(e.target as Node) && !navLinks.contains(e.target as Node)) {
      hamburger.classList.remove('active')
      navLinks.classList.remove('active')
    }
  })
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
